/* eslint-disable @typescript-eslint/consistent-type-assertions */

import { type ActorRefFrom, type MachineImplementationsFrom, raise, setup } from 'xstate5';

import { createPlaceholderAction, createPlaceholderErrorAction, createPlaceholderGuard } from './helpers'; // prettier-ignore
import { LOCATION_SEARCH_MACHINE_DEBOUNCE_DELAY_MS } from './LocationSearchMachine.constants';
import { Actions, Actors, Guards, Inputs } from './setup';
import { type Context, type Event, type Input, type PositionCoordinates } from './types';

// ─── Model ───────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    input: {} as Input,
    context: {} as Context,
    events: {} as Event,
  },
  actors: {
    getRecentAndNearbyLocations: Actors.getRecentAndNearbyLocationsActor,
    getNearbyLocations: Actors.getNearbyLocationsActor,
    searchByArea: Actors.searchByAreaActor,
    searchByGeo: Actors.searchByGeoActor,
    getPredictionsAndRestaurants: Actors.getPredictionsAndRestaurantsActor,
    selectPrediction: Actors.selectPredictionActor,
    checkIfShouldShowPickupLocationWarning: Actors.checkIfShouldShowPickupLocationWarningActor,
    checkIfShouldShowOutpostLocationWarning: Actors.checkIfShouldShowOutpostLocationWarningActor,
    searchByDeliveryLocationPlaceId: Actors.searchByDeliveryLocationPlaceIdActor,
    searchByDeliveryLocationAddress: Actors.searchByDeliveryLocationAddressActor,
    getDefaultDeliveryLocation: Actors.getDefaultDeliveryLocationActor,
    searchDeliveryAddresses: Actors.searchDeliveryAddressesActor,
    addNewDeliveryAddress: Actors.addNewDeliveryAddressActor,
  },
  guards: {
    //
    // ─── Internal Guards ─────────────────────────────────────────────────

    checkIfEnteredMinChars: Guards.checkIfEnteredMinChars,
    checkIfShouldFetchInitialLocationsByCurrentPosition:
      Guards.checkIfShouldFetchInitialLocationsByCurrentPosition, // prettier-ignore
    checkIfShouldFetchLocationsByCurrentPosition:
      Guards.checkIfShouldFetchLocationsByCurrentPosition, // prettier-ignore
    checkIfShouldShowLocationWarning: Guards.checkIfShouldShowLocationWarning,
    checkIfAcceptableSearchQuery: Guards.checkIfAcceptableSearchQuery,
    checkIfHaveRecentOrNearbyLocations: Guards.checkIfHaveRecentOrNearbyLocations,
    checkIfShouldGetDefaultDeliveryLocationOnEntry:
      Guards.checkIfShouldGetDefaultDeliveryLocationOnEntry, // prettier-ignore
    checkIfShouldAddNewDeliveryLocation: Guards.checkIfShouldAddNewDeliveryLocation,
    checkIfPickupOrderChannel: Guards.checkIfPickupOrderChannel,
    checkIfDeliveryOrderChannel: Guards.checkIfDeliveryOrderChannel,
    checkIfOutpostOrderChannel: Guards.checkIfOutpostOrderChannel,

    // ─── External Guards ─────────────────────────────────────────────────

    checkIfSignedIn: createPlaceholderGuard('checkIfSignedIn'),
    checkIfShouldShowDeliveryAddressForm:
      createPlaceholderGuard('checkIfShouldShowDeliveryAddressForm'), // prettier-ignore
  },
  actions: {
    //
    // ─── Locations Search Type ───────────────────────────────────────────

    onPickup: Actions.onPickup,
    onDelivery: Actions.onDelivery,
    onOutpost: Actions.onOutpost,

    // ─── Locations ───────────────────────────────────────────────────────

    onSearchByAreaDone: Actions.onSearchByAreaDone,
    onSearchByGeoDone: Actions.onSearchByGeoDone,
    setSearchArea: Actions.setSearchArea,
    setSearchAreaToCurrentPosition: Actions.setSearchAreaToCurrentPosition,
    setSelectedSearchArea: Actions.setSelectedSearchArea,
    clearSelectedSearchArea: Actions.clearSelectedSearchArea,
    clearSearchString: Actions.clearSearchString,
    setActiveLocation: Actions.setActiveLocation,
    clearActiveLocation: Actions.clearActiveLocation,

    // ─── Predictions ─────────────────────────────────────────────────────

    raiseSearchBySelectedPredictionGeo: Actions.raiseSearchBySelectedPredictionGeo,
    setPredictionsSearchQuery: Actions.setPredictionsSearchQuery,
    clearSearchPredictions: Actions.clearSearchPredictions,
    clearBaseLocation: Actions.clearBaseLocation,
    onSearchForPredictionsAndRestaurantsDone: Actions.onSearchForPredictionsAndRestaurantsDone, // prettier-ignore
    onSelectPredictionDone: Actions.onSelectPredictionDone,

    // ─── Prediction Tabs ─────────────────────────────────────────────────

    setActivePredictionTabForPickup: Actions.setActivePredictionTabForPickup,
    setActivePredictionTabForOutpost: Actions.setActivePredictionTabForOutpost,
    setPredictionTabToPredictions: Actions.setPredictionTabToPredictions,
    setPredictionTabToLocations: Actions.setPredictionTabToLocations,

    // ─── Location Warning ────────────────────────────────────────────────

    setLocationWarningStorageRecord: Actions.setLocationWarningStorageRecord,

    // ─── Delivery Location ───────────────────────────────────────────────

    onSearchByDeliveryPlaceIdDone: Actions.onSearchByDeliveryPlaceIdDone,
    onSearchDeliveryAddressesDone: Actions.onSearchDeliveryAddressesDone,
    onDeliveryFormFilled: Actions.onDeliveryFormFilled,
    onAddDeliveryAddressIdDone: Actions.onAddDeliveryAddressIdDone,
    setActiveRecentLocation: Actions.setActiveRecentLocation,
    setActiveRecentDeliveryLocation: Actions.setActiveRecentDeliveryLocation,
    clearDeliveryLocation: Actions.clearDeliveryLocation,
    setDeliverySearchString: Actions.setDeliverySearchString,
    clearSelectedDeliveryAddress: Actions.clearSelectedDeliveryAddress,
    setSelectedDeliveryAddressPredictionPlaceId:
      Actions.setSelectedDeliveryAddressPredictionPlaceId, // prettier-ignore
    setSelectedDeliveryAddressPrediction: Actions.setSelectedDeliveryAddressPrediction,
    setSelectedDeliveryAddressDetails: Actions.setSelectedDeliveryAddressDetails,

    // ─── Recent Locations ────────────────────────────────────────────────

    onRecentAndNearbyLocationsDone: Actions.onRecentAndNearbyLocationsDone,
    onNearbyLocationsDone: Actions.onNearbyLocationsDone,

    // ─── Focused Pin & Card ──────────────────────────────────────────────

    setFocusedPinId: Actions.setFocusedPinId,
    setFocusedCardId: Actions.setFocusedCardId,
    clearFocusedLocations: Actions.clearFocusedLocations,

    // ─── External Navigation ─────────────────────────────────────────────

    navigateToLocation: createPlaceholderAction('navigateToLocation'),
    navigateToDeliveryLocation: createPlaceholderAction('navigateToDeliveryLocation'),

    // ─── External Error Handlers ─────────────────────────────────────────

    onSearchByAreaError:
      createPlaceholderErrorAction('onSearchByAreaError', 'Failed to fetch locations by search area'), // prettier-ignore
    onCheckForLocationWarningError:
      createPlaceholderErrorAction('onCheckForLocationWarningError', 'Failed to check location warning'), // prettier-ignore
    onSearchByDeliveryPlaceIdError:
      createPlaceholderErrorAction('onSearchByDeliveryPlaceIdError', 'Failed to fetch delivery location by placed ID'), // prettier-ignore
    onSearchDeliveryAddressesError:
      createPlaceholderErrorAction('onSearchDeliveryAddressesError', 'Failed to fetch delivery addresses'), // prettier-ignore
    onAddDeliveryAddressIdError:
      createPlaceholderErrorAction('onAddDeliveryAddressIdError', 'Failed to add new delivery address'), // prettier-ignore
    onSearchPredictionsAndRestaurantsError:
      createPlaceholderErrorAction('onSearchPredictionsAndRestaurantsError', 'Failed to fetch predictions and/or restaurants'), // prettier-ignore
    onSearchByCurrentPositionError: createPlaceholderAction('onSearchByCurrentPositionError'),

    // ─── External Event Listeners ────────────────────────────────────────

    onUserLocationChanged: createPlaceholderAction('onUserLocationChanged'),
    onLocationTypeChangeEvent: createPlaceholderAction('onLocationTypeChangeEvent'),
    onSearchByAreaEvent: createPlaceholderAction('onSearchByAreaEvent'),
    onSearchByDeliveryPlaceIdEvent: createPlaceholderAction('onSearchByDeliveryPlaceIdEvent'), // prettier-ignore
    onLocationWarningConfirmEvent: createPlaceholderAction('onLocationWarningConfirmEvent'), // prettier-ignore
    onFocusedPinIdChangeEvent: createPlaceholderAction('onFocusedPinIdChangeEvent'),
    onCurrentPositionChanged: createPlaceholderAction<{
      currentPositionCoordinates?: PositionCoordinates;
    }>('onCurrentPositionChanged'),
    onNavigateToLocationEvent: createPlaceholderAction('onNavigateToLocationEvent'),
    onNavigateToDeliveryLocationEvent: createPlaceholderAction('onNavigateToDeliveryLocationEvent'), // prettier-ignore
    onSearchForPredictions: createPlaceholderAction('onSearchForPredictions'),
  },
});

// ─────────────────────────────────────────────────────────────────────────────

export const locationSearchMachine = model.createMachine({
  context: ({ input }) => ({
    //
    // ─── Per-Instance Config ─────────────────────────────────────

    client: input.client,
    lastInteractedStoreAddressId: input.lastInteractedStoreAddressId,
    shouldRequestLocationPermission: input.shouldRequestLocationPermission ?? true,
    shouldUseRadarForPickup: input.shouldUseRadarForPickup,
    shouldUseRadarForDelivery: input.shouldUseRadarForDelivery,

    // ─── Search Type ─────────────────────────────────────────────

    locationSearchType: input.locationSearchType,

    // ─── Locations ───────────────────────────────────────────────

    searchString: '',
    searchArea: undefined,
    selectedSearchArea: undefined,
    currentPosition: undefined,
    locations: undefined,
    activeLocation: undefined,

    // ─── Predictions ─────────────────────────────────────────────

    baseLocation: undefined,
    baseLocationRadiusConfig: input.baseLocationRadiusConfig,
    predictionsSearchQuery: undefined,
    predictionLocations: undefined,
    predictions: [],

    predictionTabs: ['location-prediction-tab.predictions', 'location-prediction-tab.locations'],
    activePredictionTab: 'location-prediction-tab.predictions',

    // ─── Recent And Nearby Locations ─────────────────────────────

    recentLocations: undefined,
    nearbyLocations: undefined,

    // ─── Delivery Location ───────────────────────────────────────

    deliverySearchString: '',
    deliveryLocation: undefined,
    deliveryAddressPredictions: [],
    selectedDeliveryAddressDetails: undefined,
    selectedDeliveryAddressPredictionDetails: undefined,
    selectedDeliveryAddressPredictionId: undefined,
    selectedDeliveryAddressPredictionPlaceAddress: undefined,

    // ─────────────────────────────────────────────────────────────

    focusedPinId: undefined,
    focusedLocationId: undefined,
  }),
  type: 'parallel',
  states: {
    searchType: {
      initial: 'undetermined',
      states: {
        undetermined: {
          id: 'search-type-undetermined',
          always: [
            { target: '#search-type-pickup', guard: 'checkIfPickupOrderChannel' },
            { target: '#search-type-delivery', guard: 'checkIfDeliveryOrderChannel' },
            { target: '#search-type-outpost', guard: 'checkIfOutpostOrderChannel' },
            { target: '#search-type-pickup' },
          ],
          on: {
            SET_LOCATION_SEARCH_TYPE_PICKUP: { target: '#search-type-pickup' },
            SET_LOCATION_SEARCH_TYPE_DELIVERY: { target: '#search-type-delivery' },
            SET_LOCATION_SEARCH_TYPE_OUTPOST: { target: '#search-type-outpost' },
          },
        },
        pickup: {
          id: 'search-type-pickup',
          entry: [
            'onPickup',
            'onLocationTypeChangeEvent',
            'clearFocusedLocations',
            'setActivePredictionTabForPickup',
            raise({ type: 'SEARCH_FOR_INITIAL_LOCATIONS_BY_CURRENT_POSITION' }),
          ],
          on: {
            SET_LOCATION_SEARCH_TYPE_DELIVERY: { target: '#search-type-delivery' },
            SET_LOCATION_SEARCH_TYPE_OUTPOST: { target: '#search-type-outpost' },
            SEARCH_BY_SELECTED_PREDICTION_GEO: { target: '#search-by-geo' },
            SEARCH_BY_AREA: { target: '#search-by-area' },
            SEARCH_BY_CURRENT_POSITION: [
              {
                target: '#search-by-area',
                actions: ['setSearchAreaToCurrentPosition', 'onUserLocationChanged'],
                guard: 'checkIfShouldFetchLocationsByCurrentPosition',
              },
              { actions: ['onSearchByCurrentPositionError'] },
            ],
            SET_SEARCH_AREA: { actions: ['setSearchArea'] },
            SET_FOCUSED_PIN_ID: {
              actions: ['setFocusedPinId', 'onFocusedPinIdChangeEvent'],
            },
            SET_FOCUSED_CARD_ID: { actions: ['setFocusedCardId'] },
            NAVIGATE_TO_LOCATION: {
              actions: ['setActiveLocation', 'onNavigateToLocationEvent'],
              target: '#check-for-pickup-location-warning',
            },
          },
        },
        delivery: {
          id: 'search-type-delivery',
          entry: [
            'onDelivery',
            'onLocationTypeChangeEvent',
            'clearFocusedLocations',
            raise({ type: 'SET_DEFAULT_DELIVERY_LOCATION' }),
          ],
          on: {
            SET_LOCATION_SEARCH_TYPE_PICKUP: { target: '#search-type-pickup' },
            SET_LOCATION_SEARCH_TYPE_OUTPOST: { target: '#search-type-outpost' },
            SEARCH_BY_DELIVERY_ADDRESS: { target: '#get-default-delivery-location' },
            SET_DELIVERY_SEARCH_STRING: [
              {
                target: '#search-delivery-addresses',
                actions: [
                  'setDeliverySearchString',
                  'clearDeliveryLocation',
                  'clearFocusedLocations',
                ],
                guard: 'checkIfEnteredMinChars',
              },
              {
                actions: [
                  'setDeliverySearchString',
                  'clearDeliveryLocation',
                  'clearFocusedLocations',
                ],
              },
            ],
            SET_DELIVERY_PREDICTION_PLACE_ID: {
              target: '#search-by-delivery-place-id',
              actions: [
                'clearSelectedDeliveryAddress',
                'setSelectedDeliveryAddressPredictionPlaceId',
              ],
            },
            SET_DELIVERY_PREDICTION_DETAILS: {
              target: '#search-by-delivery-location-address',
              actions: ['clearSelectedDeliveryAddress', 'setSelectedDeliveryAddressPrediction'],
            },
            SET_DELIVERY_CUSTOMER_ADDRESS: {
              target: '#search-by-delivery-place-id',
              actions: ['clearSelectedDeliveryAddress', 'setSelectedDeliveryAddressDetails'],
            },
            NAVIGATE_TO_DELIVERY_LOCATION: [
              {
                target: '#showing-delivery-address-form',
                guard: 'checkIfShouldShowDeliveryAddressForm',
              },
              {
                target: '#add-new-delivery-address',
                guard: 'checkIfShouldAddNewDeliveryLocation',
              },
              {
                target: '#navigating-to-delivery',
                actions: 'onNavigateToDeliveryLocationEvent',
              },
            ],
          },
        },
        outpost: {
          id: 'search-type-outpost',
          entry: [
            'onOutpost',
            'onLocationTypeChangeEvent',
            'clearFocusedLocations',
            'setActivePredictionTabForOutpost',
            raise({ type: 'SEARCH_FOR_INITIAL_LOCATIONS_BY_CURRENT_POSITION' }),
          ],
          on: {
            SET_LOCATION_SEARCH_TYPE_PICKUP: { target: '#search-type-pickup' },
            SET_LOCATION_SEARCH_TYPE_DELIVERY: { target: '#search-type-delivery' },
            SEARCH_BY_SELECTED_PREDICTION_GEO: { target: '#search-by-geo' },
            SEARCH_BY_AREA: { target: '#search-by-area' },
            SEARCH_BY_CURRENT_POSITION: [
              {
                target: '#search-by-area',
                actions: ['setSearchAreaToCurrentPosition', 'onUserLocationChanged'],
                guard: 'checkIfShouldFetchLocationsByCurrentPosition',
              },
              { actions: ['onSearchByCurrentPositionError'] },
            ],
            SET_SEARCH_AREA: { actions: ['setSearchArea'] },
            SET_FOCUSED_PIN_ID: { actions: ['setFocusedPinId'] },
            SET_FOCUSED_CARD_ID: { actions: ['setFocusedCardId'] },
            NAVIGATE_TO_LOCATION: {
              actions: ['setActiveLocation', 'onNavigateToLocationEvent'],
              target: '#check-for-outpost-location-warning',
            },
          },
        },
      },
    },
    search: {
      initial: 'setup',
      states: {
        setup: {
          initial: 'waiting',
          states: {
            waiting: {
              always: [
                {
                  target: '#setup-recent-and-nearby-locations',
                  guard: 'checkIfSignedIn',
                },
                {
                  target: '#setup-nearby-locations',
                },
              ],
            },
            setupRecentAndNearbyLocations: {
              id: 'setup-recent-and-nearby-locations',
              invoke: {
                src: 'getRecentAndNearbyLocations',
                input: Inputs.getInitialLocationsInput,
                onDone: {
                  target: '#finish-setup',
                  actions: [
                    { type: 'onRecentAndNearbyLocationsDone', params: ({ event }) => event.output }, // prettier-ignore
                    { type: 'onCurrentPositionChanged', params: ({ event }) => event.output },
                  ],
                },
                onError: {
                  target: '#idle',
                },
              },
              after: {
                10_000: '#idle',
              },
              on: {
                // NOTE: Skip setup when customer searches for predictions during setup.
                SEARCH_FOR_PREDICTIONS: {
                  target: '#idle',
                },
              },
            },
            setupNearbyLocations: {
              id: 'setup-nearby-locations',
              invoke: {
                src: 'getNearbyLocations',
                input: Inputs.getInitialLocationsInput,
                onDone: {
                  target: '#finish-setup',
                  actions: [
                    { type: 'onNearbyLocationsDone', params: ({ event }) => event.output },
                    { type: 'onCurrentPositionChanged', params: ({ event }) => event.output },
                  ],
                },
                onError: {
                  target: '#idle',
                },
              },
              after: {
                100_000: '#idle',
              },
              on: {
                // NOTE: Skip setup when customer searches for predictions during setup.
                SEARCH_FOR_PREDICTIONS: {
                  target: '#idle',
                },
              },
            },
            finishSetup: {
              id: 'finish-setup',
              always: [
                {
                  target: '#recent-and-nearby-locations',
                  guard: 'checkIfHaveRecentOrNearbyLocations',
                },
                {
                  target: '#idle',
                },
              ],
              type: 'final',
            },
          },
        },
        idle: {
          id: 'idle',
          on: {
            SET_DEFAULT_DELIVERY_LOCATION: {
              target: '#get-default-delivery-location',
              guard: 'checkIfShouldGetDefaultDeliveryLocationOnEntry',
            },
            SEARCH_FOR_INITIAL_LOCATIONS_BY_CURRENT_POSITION: {
              target: '#search-by-area',
              guard: 'checkIfShouldFetchInitialLocationsByCurrentPosition',
            },
            SHOW_RECENT_AND_NEARBY_LOCATIONS: {
              target: '#recent-and-nearby-locations',
              guard: 'checkIfHaveRecentOrNearbyLocations',
            },
          },
        },
        showingDeliveryAddressForm: {
          id: 'showing-delivery-address-form',
          on: {
            CANCEL_DELIVERY_ADDRESS_FORM: 'idle',
            SUBMIT_DELIVERY_ADDRESS_FORM: {
              actions: ['onDeliveryFormFilled'],
              target: '#add-new-delivery-address',
            },
          },
        },
        recentAndNearbyLocations: {
          id: 'recent-and-nearby-locations',
          initial: 'idle',
          entry: ['clearBaseLocation'],
          states: {
            idle: {
              id: 'recent-and-nearby-locations-idle',
              on: {
                NAVIGATE_TO_RECENT_PICKUP_LOCATION: {
                  actions: ['setActiveRecentLocation', 'onNavigateToLocationEvent'],
                  target: '#recent-and-nearby-locations-check-pickup-location-warning',
                },
                NAVIGATE_TO_RECENT_OUTPOST_LOCATION: {
                  actions: ['setActiveRecentLocation', 'onNavigateToLocationEvent'],
                  target: '#recent-and-nearby-locations-check-outpost-location-warning',
                },
                NAVIGATE_TO_RECENT_DELIVERY_LOCATION: [
                  {
                    actions: [
                      'setActiveRecentDeliveryLocation',
                      'onNavigateToDeliveryLocationEvent',
                    ],
                    target: '#recent-and-nearby-locations-navigating-to-delivery',
                  },
                ],
              },
            },
            checking: {
              initial: 'waiting',
              states: {
                waiting: {
                  description: 'A placeholder state',
                },
                pickupLocation: {
                  id: 'recent-and-nearby-locations-check-pickup-location-warning',
                  invoke: {
                    src: 'checkIfShouldShowPickupLocationWarning',
                    input: Inputs.getLocationWarningInput,
                    onDone: [
                      {
                        target: '#recent-and-nearby-locations-warning-pickup-location',
                        guard: {
                          type: 'checkIfShouldShowLocationWarning',
                          params: ({ event }) => event.output,
                        },
                      },
                      {
                        target: '#recent-and-nearby-locations-navigating-to-pickup',
                      },
                    ],
                    onError: {
                      target: '#recent-and-nearby-locations-idle',
                      actions: ['onCheckForLocationWarningError'],
                    },
                  },
                },
                outpostLocation: {
                  id: 'recent-and-nearby-locations-check-outpost-location-warning',
                  invoke: {
                    src: 'checkIfShouldShowOutpostLocationWarning',
                    input: Inputs.getLocationWarningInput,
                    onDone: [
                      {
                        target: '#recent-and-nearby-locations-warning-outpost-location',
                        guard: {
                          type: 'checkIfShouldShowLocationWarning',
                          params: ({ event }) => event.output,
                        },
                      },
                      {
                        target: '#recent-and-nearby-locations-navigating-to-outpost',
                      },
                    ],
                    onError: {
                      target: '#recent-and-nearby-locations-idle',
                      actions: ['onCheckForLocationWarningError'],
                    },
                  },
                },
              },
            },
            warning: {
              on: {
                LOCATION_WARNING_CANCEL: {
                  target: '#recent-and-nearby-locations-idle',
                  actions: ['clearActiveLocation'],
                },
              },
              initial: 'waiting',
              states: {
                waiting: {
                  description: 'A placeholder state',
                },
                pickupLocation: {
                  id: 'recent-and-nearby-locations-warning-pickup-location',
                  on: {
                    LOCATION_WARNING_CONFIRM: {
                      target: '#recent-and-nearby-locations-navigating-to-pickup',
                      actions: ['setLocationWarningStorageRecord', 'onLocationWarningConfirmEvent'],
                    },
                  },
                },
                outpostLocation: {
                  id: 'recent-and-nearby-locations-warning-outpost-location',
                  on: {
                    LOCATION_WARNING_CONFIRM: {
                      target: '#recent-and-nearby-locations-navigating-to-outpost',
                      actions: ['setLocationWarningStorageRecord', 'onLocationWarningConfirmEvent'],
                    },
                  },
                },
              },
            },
            navigating: {
              initial: 'waiting',
              states: {
                waiting: {
                  description: 'A placeholder state',
                },
                toPickup: {
                  id: 'recent-and-nearby-locations-navigating-to-pickup',
                  entry: ['navigateToLocation'],
                  always: { target: '#recent-and-nearby-locations-idle' },
                },
                toOutpost: {
                  id: 'recent-and-nearby-locations-navigating-to-outpost',
                  entry: ['navigateToLocation'],
                  always: { target: '#recent-and-nearby-locations-idle' },
                },
                toDelivery: {
                  id: 'recent-and-nearby-locations-navigating-to-delivery',
                  entry: ['navigateToDeliveryLocation'],
                  always: { target: '#recent-and-nearby-locations-idle' },
                },
              },
            },
          },
        },
        loading: {
          initial: 'waiting',
          states: {
            waiting: {
              description: 'A placeholder state',
            },

            // ─── Locations ───────────────────────────────

            searchByArea: {
              id: 'search-by-area',
              entry: ['clearSearchString', 'setSelectedSearchArea'],
              invoke: {
                src: 'searchByArea',
                input: Inputs.getSearchByAreaInput,
                onDone: {
                  target: '#idle',
                  actions: [
                    { type: 'onSearchByAreaDone', params: ({ event }) => event.output },
                    'onSearchByAreaEvent',
                    'clearFocusedLocations',
                  ],
                },
                onError: {
                  target: '#idle',
                  actions: ['onSearchByAreaError'],
                },
              },
            },
            searchByGeo: {
              id: 'search-by-geo',
              invoke: {
                src: 'searchByGeo',
                input: Inputs.getSearchByGeoInput,
                onDone: {
                  target: '#idle',
                  actions: [
                    { type: 'onSearchByGeoDone', params: ({ event }) => event.output },
                    'clearFocusedLocations',
                  ],
                },
                onError: {
                  target: '#idle',
                },
              },
            },

            // ─── Locations Warnings ──────────────────────

            checkForPickupLocationWarning: {
              id: 'check-for-pickup-location-warning',
              invoke: {
                src: 'checkIfShouldShowPickupLocationWarning',
                input: Inputs.getLocationWarningInput,
                onDone: [
                  {
                    target: '#warning-pickup-location',
                    guard: {
                      type: 'checkIfShouldShowLocationWarning',
                      params: ({ event }) => event.output,
                    },
                  },
                  {
                    target: '#navigating-to-pickup',
                  },
                ],
                onError: {
                  target: '#idle',
                  actions: ['onCheckForLocationWarningError'],
                },
              },
            },
            checkForOutpostLocationWarning: {
              id: 'check-for-outpost-location-warning',
              invoke: {
                src: 'checkIfShouldShowOutpostLocationWarning',
                input: Inputs.getLocationWarningInput,
                onDone: [
                  {
                    target: '#warning-outpost-location',
                    guard: {
                      type: 'checkIfShouldShowLocationWarning',
                      params: ({ event }) => event.output,
                    },
                  },
                  {
                    target: '#navigating-to-outpost',
                  },
                ],
                onError: {
                  target: '#idle',
                  actions: ['onCheckForLocationWarningError'],
                },
              },
            },

            // ─── Delivery Location ───────────────────────

            searchByDeliveryLocationPlaceId: {
              id: 'search-by-delivery-place-id',
              invoke: {
                src: 'searchByDeliveryLocationPlaceId',
                input: Inputs.getSearchByDeliveryPlaceIdInput,
                onDone: {
                  target: '#idle',
                  actions: [
                    'clearFocusedLocations',
                    { type: 'onSearchByDeliveryPlaceIdDone', params: ({ event }) => event.output }, // prettier-ignore
                    'onSearchByDeliveryPlaceIdEvent',
                  ],
                },
                onError: {
                  target: '#idle',
                  actions: ['onSearchByDeliveryPlaceIdError'],
                },
              },
            },
            searchByDeliveryLocationAddress: {
              id: 'search-by-delivery-location-address',
              invoke: {
                src: 'searchByDeliveryLocationAddress',
                input: Inputs.getSearchByDeliveryAddressInput,
                onDone: {
                  target: '#idle',
                  actions: [
                    'clearFocusedLocations',
                    { type: 'onSearchByDeliveryPlaceIdDone', params: ({ event }) => event.output }, // prettier-ignore
                    'onSearchByDeliveryPlaceIdEvent',
                  ],
                },
                onError: {
                  target: '#idle',
                  actions: ['onSearchByDeliveryPlaceIdError'],
                },
              },
            },
            getDefaultDeliveryLocation: {
              id: 'get-default-delivery-location',
              invoke: {
                src: 'getDefaultDeliveryLocation',
                input: Inputs.getDefaultDeliveryLocationInput,
                onDone: {
                  target: '#idle',
                  actions: [
                    'clearFocusedLocations',
                    { type: 'onSearchByDeliveryPlaceIdDone', params: ({ event }) => event.output }, // prettier-ignore
                    'onSearchByDeliveryPlaceIdEvent',
                  ],
                },
                onError: {
                  target: '#idle',
                  actions: ['onSearchByDeliveryPlaceIdError'],
                },
              },
            },
            searchDeliveryAddresses: {
              id: 'search-delivery-addresses',
              invoke: {
                src: 'searchDeliveryAddresses',
                input: Inputs.getSearchDeliveryAddressesInput,
                onDone: {
                  target: '#idle',
                  actions: [
                    { type: 'onSearchDeliveryAddressesDone', params: ({ event }) => event.output }, // prettier-ignore
                  ],
                },
                onError: {
                  target: '#idle',
                  actions: ['onSearchDeliveryAddressesError'],
                },
              },
            },
            addNewDeliveryAddress: {
              id: 'add-new-delivery-address',
              invoke: {
                src: 'addNewDeliveryAddress',
                input: Inputs.getAddNewDeliveryAddressInput,
                onDone: {
                  target: '#navigating-to-delivery',
                  actions: [
                    { type: 'onAddDeliveryAddressIdDone', params: ({ event }) => event.output },
                  ],
                },
                onError: {
                  target: '#idle',
                  actions: ['onAddDeliveryAddressIdError'],
                },
              },
            },
          },
        },
        warning: {
          initial: 'waiting',
          states: {
            waiting: {
              description: 'A placeholder state',
            },
            pickupLocation: {
              id: 'warning-pickup-location',
              on: {
                LOCATION_WARNING_CANCEL: {
                  target: '#idle',
                  actions: ['clearActiveLocation'],
                },
                LOCATION_WARNING_CONFIRM: {
                  target: '#navigating-to-pickup',
                  actions: ['setLocationWarningStorageRecord', 'onLocationWarningConfirmEvent'],
                },
              },
            },
            outpostLocation: {
              id: 'warning-outpost-location',
              on: {
                LOCATION_WARNING_CANCEL: {
                  target: '#idle',
                  actions: ['clearActiveLocation'],
                },
                LOCATION_WARNING_CONFIRM: {
                  target: '#navigating-to-outpost',
                  actions: ['setLocationWarningStorageRecord', 'onLocationWarningConfirmEvent'],
                },
              },
            },
          },
        },
        navigating: {
          initial: 'waiting',
          states: {
            waiting: {
              description: 'A placeholder state',
            },
            toPickup: {
              id: 'navigating-to-pickup',
              entry: ['navigateToLocation'],
              always: { target: '#idle' },
            },
            toOutpost: {
              id: 'navigating-to-outpost',
              entry: ['navigateToLocation'],
              always: { target: '#idle' },
            },
            toDelivery: {
              id: 'navigating-to-delivery',
              entry: ['navigateToDeliveryLocation'],
              always: { target: '#idle' },
            },
          },
        },
      },
    },
    searchForPredictions: {
      description: 'A state to control the predictions search flow',
      id: 'search-for-predictions',
      on: {
        SEARCH_BY_AREA: '.resetting',
        SEARCH_BY_CURRENT_POSITION: '.resetting',
        SELECT_PREDICTION: '.selecting',

        SET_PREDICTION_TAB_TO_PREDICTIONS: {
          actions: ['setPredictionTabToPredictions'],
        },
        SET_PREDICTION_TAB_TO_LOCATIONS: {
          actions: ['setPredictionTabToLocations'],
        },
      },
      initial: 'idle',
      states: {
        idle: {
          on: {
            SEARCH_FOR_PREDICTIONS: {
              target: 'determining',
              actions: ['setPredictionsSearchQuery'],
            },
          },
        },
        determining: {
          always: [
            {
              target: 'debouncing',
              guard: 'checkIfAcceptableSearchQuery',
            },
            {
              target: 'idle',
              actions: ['clearSearchPredictions'],
            },
          ],
        },
        debouncing: {
          after: {
            [LOCATION_SEARCH_MACHINE_DEBOUNCE_DELAY_MS]: 'searching',
          },
          on: {
            SEARCH_FOR_PREDICTIONS: {
              target: 'determining',
              actions: ['setPredictionsSearchQuery'],
            },
          },
        },
        searching: {
          on: {
            SEARCH_FOR_PREDICTIONS: {
              target: 'determining',
              actions: ['setPredictionsSearchQuery'],
            },
          },
          entry: ['onSearchForPredictions'],
          invoke: {
            src: 'getPredictionsAndRestaurants',
            input: Inputs.getPredictionsAndRestaurantsInput,
            onDone: {
              target: 'idle',
              actions: [
                { type: 'onSearchForPredictionsAndRestaurantsDone', params: ({ event }) => event.output }, // prettier-ignore
              ],
            },
            onError: {
              target: 'resetting',
              actions: ['onSearchPredictionsAndRestaurantsError'],
            },
          },
        },
        selecting: {
          entry: ['clearSelectedSearchArea'],
          invoke: {
            src: 'selectPrediction',
            input: Inputs.getSelectPredictionInput,
            onDone: {
              target: 'idle',
              actions: [
                'clearSearchPredictions',
                { type: 'onSelectPredictionDone', params: ({ event }) => event.output },
                'raiseSearchBySelectedPredictionGeo',
              ],
            },
            onError: {
              target: 'resetting',
            },
          },
        },
        resetting: {
          always: { target: 'idle' },
          entry: ['clearSearchPredictions', 'clearBaseLocation'],
        },
      },
    },
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type LocationSearchMachineActorRef = ActorRefFrom<typeof locationSearchMachine>;
export type LocationSearchMachineImplementations = MachineImplementationsFrom<typeof locationSearchMachine>; // prettier-ignore

/**
 * Back-compat alias. Consumers historically referenced the interpreter type as
 * `LocationSearchService`.
 */
export type LocationSearchService = LocationSearchMachineActorRef;
